import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LottieAnimation, TopicIcon } from 'src/components';
import { SessionType } from 'src/api/learningSessions/types';
import { IconName } from 'src/components/Icon';
import { defaultAnimationOptions } from 'src/components/constants';
import { ScenarioType } from 'src/api/userScenario/types';
import { ActivityType } from 'src/api/me/types';
import premiumScenarioCompletedAnimation from './premiumScenarioCompletedCelebration.lottie.json';
import reviewSessionCompletedAnimation from './reviewSessionCompleted.lottie.json';
import scenarioCompletedCelebration from './scenarioCompletedCelebration.lottie.json';
import * as S from './styles';

const msgs = defineMessages({
  wordsAndPhrasesRatio: {
    id: 'scenario_words_and_phrases_count',
  },
  wordsAndPhrasesTotal: {
    id: 'scenario_words_and_phrases_total',
  },
  congratulations: {
    id: 'web_paid_confirm_header',
  },
});

interface Props {
  title?: string;
  topic?: string;
  iconSrc?: string;
  iconName?: IconName;
  itemsTotal?: number;
  itemsCompleted?: number;
  isPremiumScenario?: boolean;
  scenarioType?: ScenarioType;
  sessionType?: SessionType;
  showAnimation?: boolean;
  userScenarioCompleted?: boolean;
  reviewSessionCompleted?: boolean;
  activityType?: ActivityType;
}

export const ScenarioHeader: FC<Props> = ({
  iconSrc,
  iconName,
  title,
  topic,
  scenarioType,
  sessionType,
  itemsTotal,
  itemsCompleted = 0,
  isPremiumScenario,
  showAnimation,
  userScenarioCompleted,
  reviewSessionCompleted,
  activityType,
}) => {
  const intl = useIntl();
  const showTopicIcon = sessionType === SessionType.LEARN || scenarioType;
  const learnActivity = activityType === 'learn';

  const animationScenarioCompleted = (
    <LottieAnimation
      {...defaultAnimationOptions}
      animationData={
        isPremiumScenario ? premiumScenarioCompletedAnimation : scenarioCompletedCelebration
      }
    />
  );

  const animationReviewCompleted = (
    <LottieAnimation {...defaultAnimationOptions} animationData={reviewSessionCompletedAnimation} />
  );

  const showScenarioCompletedAnimation = showAnimation && userScenarioCompleted;
  const showReviewSessionCompletedAnimation = showAnimation && reviewSessionCompleted;

  const topicIcon = (
    <TopicIcon
      iconSrc={scenarioType === 'immerse' ? undefined : iconSrc}
      iconName={scenarioType === 'immerse' ? 'videoLibraryFilled' : iconName}
      size="s20"
      showBadge={!showScenarioCompletedAnimation && isPremiumScenario}
    />
  );

  return (
    <S.Wrapper $showAnimation={!!showAnimation} $align="center" data-testid="scenario-header">
      {!showAnimation && showTopicIcon && topicIcon}
      {showScenarioCompletedAnimation && (
        <S.AnimationContainer
          data-testid={`scenario-completed-animation${isPremiumScenario ? '-premium' : ''}`}
        >
          {animationScenarioCompleted}
          {!learnActivity && <S.IconContainer>{topicIcon}</S.IconContainer>}
        </S.AnimationContainer>
      )}
      {showReviewSessionCompletedAnimation && (
        <S.AnimationContainer data-testid="review-completed-animation">
          {animationReviewCompleted}
        </S.AnimationContainer>
      )}
      {showAnimation && !activityType && (
        <S.AnimationTitle>{intl.formatMessage(msgs.congratulations)}</S.AnimationTitle>
      )}
      {topic && (
        <S.Topic $isPremiumScenario={isPremiumScenario} $completed={scenarioType === 'completed'}>
          {topic}
        </S.Topic>
      )}
      {!showAnimation && <S.Title dir="auto">{title}</S.Title>}
      {itemsTotal && (
        <S.AdditionalText>
          {scenarioType === 'up_next'
            ? intl.formatMessage(msgs.wordsAndPhrasesTotal, {
                itemsTotal,
              })
            : intl.formatMessage(msgs.wordsAndPhrasesRatio, {
                itemsCompleted,
                itemsTotal,
              })}
        </S.AdditionalText>
      )}
    </S.Wrapper>
  );
};
